import PropTypes from "prop-types";
import React, { useRef } from "react";
import CopyIcon from "@mui/icons-material/FilterNone";
import ImportIcon from "@mui/icons-material/SaveAlt";
import Modal from "../../Modal";
import { visionURL } from "../../../config/environment";
import {
  Typography,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Box
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";

const useStyles = makeStyles(theme => ({
  subtitle: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2)
  },
  divider: {
    textAlign: "center",
    marginTop: theme.spacing(4),
    marginBottom: -theme.spacing(2)
  },
  downloadButton: {
    marginLeft: theme.spacing(-1),
    textTransform: "none"
  }
}));

const CalendarSelectMenu = ({ currentValue, options, onCalendarChange }) => {
  return (
    <TextField
      select
      variant="filled"
      fullWidth
      value={currentValue}
      onChange={ev => {
        onCalendarChange(ev.target.value);
      }}
      SelectProps={{
        renderValue: value => value.name
      }}
      hiddenLabel
    >
      {options.map((option, index) => (
        <MenuItem key={option.name + index} value={option}>
          {option.name}
        </MenuItem>
      ))}
    </TextField>
  );
};

const CalendarExportForm = ({ trainingCalendarList, calendarFeedLink }) => {
  const classes = useStyles();
  const options = getCalendarOptions(trainingCalendarList);
  const [calendar, setCalendar] = React.useState(options[0]);
  const urlRef = useRef(null);
  function copyUrl() {
    urlRef.current.select();
    urlRef.current.setSelectionRange(0, 99999); /*For mobile devices*/
    document.execCommand("copy");
  }

  function handleCalendarChange(newCalendar) {
    console.log("newCalendar", newCalendar);
    setCalendar(newCalendar);
  }

  console.log("calendar", calendar);

  return (
    <div className={classes.root}>
      <CalendarSelectMenu
        onCalendarChange={handleCalendarChange}
        options={options}
        currentValue={calendar}
      />
      <Typography
        className={classes.subtitle}
        variant="h5"
        color="textSecondary"
      >
        By URL
      </Typography>
      <Box mb={2}>
        <Typography variant="body2" color="textSecondary">
          Copy and paste the following URL into any external calendar
          application to subscribe to events for the seleted calendar(s).
        </Typography>
      </Box>
      {!calendarFeedLink ? (
        <LoadingSpinner size={50} containerHeight={120} />
      ) : (
        <TextField
          variant="filled"
          fullWidth
          multiline
          inputRef={urlRef}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={copyUrl} size="large">
                  <CopyIcon />
                </IconButton>
              </InputAdornment>
            ),
            readOnly: true
          }}
          value={calendarFeedLink + `?value=${calendar.value}`}
          hiddenLabel
        />
      )}
      <Typography
        className={classes.divider}
        variant="h5"
        color="textSecondary"
      >
        &mdash; OR &mdash;
      </Typography>
      <Typography
        className={classes.subtitle}
        variant="h5"
        color="textSecondary"
      >
        By File
      </Typography>
      <Button
        color="primary"
        className={classes.downloadButton}
        startIcon={<ImportIcon />}
        type="submit"
        onClick={() => downloadCalendar(calendar.value)}
      >
        Download .ics
      </Button>
    </div>
  );
};

/**
 * Returns the list of calendar options to export which includes the group for all training calendars
 * and each calendar within that group
 */
const getCalendarOptions = trainingCalendarList => {
  if (trainingCalendarList.calendars.length > 1) {
    return [
      {
        name: "All Training Calendars",
        eventFeedUrl: trainingCalendarList.eventFeedUrl,
        value: "all"
      },
      ...trainingCalendarList.calendars.map(calendar => {
        return {
          name: calendar.name,
          eventFeedUrl: calendar.eventFeedUrl,
          value: calendar.id
        };
      })
    ];
  } else {
    return [
      ...trainingCalendarList.calendars.map(calendar => {
        return {
          name: calendar.name,
          eventFeedUrl: calendar.eventFeedUrl,
          value: calendar.id
        };
      })
    ];
  }
};

const downloadCalendar = calendarValue => {
  let url = `${visionURL}/learning_center/api/calendars/export?value=${calendarValue}`;
  window.open(url);
};

const ExportCalendar = ({
  onOpenChange,
  open,
  trainingCalendarList,
  calendarFeedLink
}) => {
  return (
    <Modal
      showCloseIcon
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {trainingCalendarList.calendars.length > 1
            ? "Export Calendars"
            : "Export Calendar"}
        </Typography>
      }
      open={open}
      onClose={() => onOpenChange(false)}
      dialogContent={
        <CalendarExportForm
          trainingCalendarList={trainingCalendarList}
          calendarFeedLink={calendarFeedLink}
        />
      }
      dialogActions={
        <Button
          color="primary"
          onClick={() => {
            onOpenChange(false);
          }}
        >
          Cancel
        </Button>
      }
      maxWidth="sm"
      fullWidth
      scrollable
    />
  );
};

ExportCalendar.propTypes = {
  /**
   * True if the export modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the export modal is open or closed
   */
  onOpenChange: PropTypes.func,
  trainingCalendarList: PropTypes.shape({
    /**
     * The URL for subscribing to events for all training calendars
     */
    eventFeedUrl: PropTypes.string,
    calendars: PropTypes.arrayOf(
      PropTypes.shape({
        /**
         * The name of the training calendar
         */
        name: PropTypes.string,
        /**
         * The URL for subscribing to events for the individual training calendar
         */
        eventFeedUrl: PropTypes.string
      })
    )
  })
};
export default ExportCalendar;
